import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  lazy,
  Suspense
} from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Alert from './caseTable/Alert';
import { SlNote } from "react-icons/sl";
import {
  FiLock,
  FiGlobe,
  FiEye,
  FiEyeOff,
  FiUser,
  FiCalendar,
  FiPlus,
  FiMessageSquare,
  FiShield,
  FiFileText,
  FiClock,
  FiAtSign,
  FiPaperclip,
  FiX,
  FiDownload
} from "react-icons/fi";
import DOMPurify from 'dompurify';
import Spinner from 'react-bootstrap/Spinner';
import Toast from 'react-bootstrap/Toast';
import Task from './caseTable/Task';
import CaseTable from './caseTable/CaseTable';
import Modal from 'react-modal';
import UserMentionPicker from '../../../../mention/UserMentionPicker';
// ✅ NEW: emoji reactions on notes (only mentioned users can react)
import NoteReactions from '../../../../../pages/NoteReactions';

const ReactQuill = lazy(() =>
  Promise.all([
    import('react-quill'),
    import('react-quill/dist/quill.snow.css')
  ]).then(([mod]) => mod)
);

Modal.setAppElement('#root');

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const QUILL_MODULES = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ color: [] }, { background: [] }],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' }
    ],
    [{ align: [] }],
    ['link', 'image'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const QUILL_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'color',
  'background',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'align'
];

const stripHtml = (html) =>
  html
    ? html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, ' ').trim()
    : '';

const toBool = (v) => v === true || v === 'true' || v === 1 || v === '1';

const sortByCreatedAt = (arr) =>
  arr.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const isImage = (mimetype) => (mimetype || '').startsWith('image/');

const getAttachmentUrl = (att) => {
  if (!att) return '';
  if (att.url) {
    return att.url.startsWith('http') ? att.url : `${API_BASE_URL}${att.url}`;
  }
  if (att.path) {
    return `${API_BASE_URL}/uploads/notes/${att.name || ''}`;
  }
  return '';
};

// Clipboard screenshots usually come as a generic "image.png" - give them a unique name
let pastedCounter = 0;
const normalizePastedFile = (f) => {
  const generic = !f.name || /^image\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name);
  if (!generic) return f;

  pastedCounter += 1;
  const ext = ((f.type || 'image/png').split('/')[1] || 'png')
    .replace('jpeg', 'jpg')
    .replace('svg+xml', 'svg');

  return new File([f], `pasted-${Date.now()}-${pastedCounter}.${ext}`, {
    type: f.type,
    lastModified: f.lastModified || Date.now()
  });
};

/* Small thumbnail for selected (not yet uploaded) files */
const FileThumb = ({ file }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!file || !isImage(file.type)) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  if (!url) return <FiFileText size={14} color="#0066cc" />;

  return <img src={url} alt="" style={styles.fileChipThumb} />;
};

/* ============================================================
   NOTE ITEM (memoized - only re-renders when its own note changes)
   ============================================================ */
const NoteItem = React.memo(function NoteItem({
  note,
  isAdmin,
  isMentionedForMe,
  onToggleVisibility,
  onToggleInternal,
  caseId,              // ✅ NEW
  myUserIdNum,         // ✅ NEW
  onReactionsUpdated   // ✅ NEW
}) {
  const isInternal = toBool(note.isInternal);
  const isHidden = toBool(note.isHidden);

  const hiddenTime = note.hiddenAt
    ? new Date(note.hiddenAt).toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Unknown';

  const shouldBlur = isHidden && !isAdmin;

  const showThisInternalNote = isInternal
    ? note.showInternalContent !== false
    : true;

  const safeHtml = useMemo(
    () => DOMPurify.sanitize(note.text || ''),
    [note.text]
  );

  return (
    <div
      id={note._id}
      style={{
        ...styles.noteItem,
        borderLeftColor: isMentionedForMe
          ? '#ffc107'
          : isInternal
          ? '#ffc107'
          : '#17a2b8',
        background: isHidden
          ? '#fff9f9'
          : isMentionedForMe
          ? '#fffbe6'
          : 'white',
        ...(isHidden && { borderLeftColor: '#dc3545' }),
        ...(isMentionedForMe && styles.noteItemMentioned)
      }}
    >
      {isAdmin && isHidden && (
        <div style={styles.adminOverlay}>
          <FiEyeOff size={10} />
          Hidden from users
        </div>
      )}

      {isMentionedForMe && (
        <div style={styles.mentionBadge}>
          <FiAtSign size={11} /> Mentioned you
        </div>
      )}

      <div style={styles.noteHeader}>
        {isInternal && (
          <label
            style={styles.internalToggleCheckbox}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={showThisInternalNote}
              onChange={(e) => {
                e.stopPropagation();
                onToggleInternal(note._id, e);
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <span>Show</span>
          </label>
        )}

        <span
          style={{
            ...styles.noteBadge,
            ...(isInternal ? styles.internalBadge : styles.externalBadge)
          }}
        >
          {isInternal ? <FiLock size={11} /> : <FiGlobe size={11} />}
          {isInternal ? 'Internal' : 'External'}
        </span>

        {isHidden && (
          <span style={{ ...styles.noteBadge, ...styles.hiddenBadge }}>
            <FiEyeOff size={11} />
            Hidden
          </span>
        )}

        {isAdmin && (
          <span style={{ ...styles.noteBadge, ...styles.adminBadge }}>
            <FiShield size={11} />
            Admin
          </span>
        )}

        <span style={styles.separator}>•</span>

        <span>
          <FiUser size={12} /> {note.createdBy || 'Unknown'}
        </span>

        {note.createdByUserId && (
          <>
            <span style={styles.separator}>•</span>
            <span style={{ fontSize: '11px', color: '#adb5bd' }}>
              ID: {note.createdByUserId}
            </span>
          </>
        )}

        <span style={styles.separator}>•</span>

        <span>
          <FiCalendar size={12} />{' '}
          {note.createdAt
            ? new Date(note.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })
            : 'Unknown'}
        </span>

        <span style={styles.separator}>•</span>

        <span style={{ fontSize: '12px' }}>
          {note.createdAt
            ? new Date(note.createdAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })
            : ''}
        </span>

        {isHidden && note.hiddenBy && (
          <>
            <span style={styles.separator}>•</span>
            <span
              style={{
                fontSize: '11px',
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FiEyeOff size={10} />
              Hidden by: {note.hiddenBy}
            </span>
          </>
        )}

        {isHidden && note.hiddenAt && (
          <>
            <span style={styles.separator}>•</span>
            <span
              style={{
                fontSize: '11px',
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FiClock size={10} />
              {hiddenTime}
            </span>
          </>
        )}
      </div>

      {Array.isArray(note.mentions) && note.mentions.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: '8px',
            fontSize: '12px',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              color: '#6c757d',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <FiAtSign size={12} /> Mentioned:
          </span>
          {note.mentions.map((m) => (
            <span
              key={m.userId || m.userName}
              style={{
                background: '#e8f4f8',
                color: '#0056b3',
                borderRadius: '12px',
                padding: '2px 8px',
                fontWeight: 600,
                border: '1px solid #b8daff'
              }}
            >
              @{m.userName}
            </span>
          ))}
        </div>
      )}

      <div style={styles.noteContent}>
        {isInternal && !showThisInternalNote ? (
          <div style={styles.hiddenInternalNote}>
            Internal note is hidden.
            <br />
            <span style={{ fontSize: '11px' }}>
              Enable the checkbox to view this note.
            </span>
          </div>
        ) : (
          <>
            <div style={shouldBlur ? styles.blurContent : {}}>
              <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
            </div>

            {/* attachments */}
            {Array.isArray(note.attachments) && note.attachments.length > 0 && (
              <div style={styles.attachmentsWrap}>
                <div style={styles.attachmentsLabel}>
                  <FiPaperclip size={12} /> Attachments ({note.attachments.length})
                </div>

                <div style={styles.attachmentsGrid}>
                  {note.attachments.map((att) => {
                    const href = getAttachmentUrl(att);
                    const displayName = att.originalName || att.name;

                    /* ---- IMAGE: large preview ---- */
                    if (isImage(att.mimetype)) {
                      return (
                        <div
                          key={att._id || att.name}
                          style={styles.attachmentCardImage}
                        >
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.attachmentThumbLinkLarge}
                            title="Open full size"
                          >
                            <img
                              src={href}
                              alt={displayName}
                              style={styles.attachmentThumbLarge}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </a>

                          <div style={styles.attachmentImageFooter}>
                            <div style={styles.attachmentInfo}>
                              <div
                                style={styles.attachmentName}
                                title={displayName}
                              >
                                {displayName}
                              </div>
                              <div style={styles.attachmentMeta}>
                                {formatFileSize(att.size)}
                              </div>
                            </div>

                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={displayName}
                              style={styles.attachmentDownload}
                              title="Download"
                            >
                              <FiDownload size={14} />
                            </a>
                          </div>
                        </div>
                      );
                    }

                    /* ---- OTHER FILES ---- */
                    return (
                      <div key={att._id || att.name} style={styles.attachmentCard}>
                        <div style={styles.attachmentIconBox}>
                          <FiFileText size={20} color="#0066cc" />
                        </div>

                        <div style={styles.attachmentInfo}>
                          <div
                            style={styles.attachmentName}
                            title={displayName}
                          >
                            {displayName}
                          </div>
                          <div style={styles.attachmentMeta}>
                            {formatFileSize(att.size)}
                          </div>
                        </div>

                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={displayName}
                          style={styles.attachmentDownload}
                          title="Download"
                        >
                          <FiDownload size={14} />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {shouldBlur && <div style={styles.hiddenOverlay} />}
          </>
        )}
      </div>

      {/* ✅ NEW: emoji reactions (only the mentioned user can react; everyone sees them) */}
      {!(isInternal && !showThisInternalNote) && (
        <NoteReactions
          caseId={caseId}
          noteId={note._id}
          reactions={note.reactions}
          canReact={isMentionedForMe}
          myUserId={myUserIdNum}
          onUpdated={onReactionsUpdated}
        />
      )}

      <div style={styles.noteActions}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isAdmin && (
            <button
              style={styles.actionBtnAdmin}
              onClick={() => onToggleVisibility(note._id)}
              title={
                isHidden
                  ? 'Make visible to all users'
                  : 'Hide from all users'
              }
            >
              {isHidden ? <FiEye size={13} /> : <FiEyeOff size={13} />}
              {isHidden ? 'Show to All' : 'Hide from All'}
            </button>
          )}

          {!isAdmin && isHidden && (
            <span style={styles.hiddenStatus}>
              <FiShield size={12} />
              This note is hidden from users
            </span>
          )}

          {isAdmin && !isHidden && (
            <span style={styles.visibleStatus}>
              <FiShield size={12} />
              Visible to all users
            </span>
          )}
        </div>

        <span style={{ fontSize: '11px', color: '#868e96' }}>
          {isInternal ? 'For Internal Use' : 'For External Use'}
        </span>
      </div>
    </div>
  );
});

/* ============================================================
   ADD NOTE MODAL
   ============================================================ */
const AddNoteModal = React.memo(function AddNoteModal({
  isOpen,
  onClose,
  onSave,
  isLoading,
  defaultRecipient
}) {
  const [newNoteText, setNewNoteText] = useState('');
  const [subject, setSubject] = useState('case fabrication');
  const [recipient, setRecipient] = useState(defaultRecipient || '');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [mentions, setMentions] = useState([]);

  // files: [{ file, id }]
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // editor wrapper element (callback-ref via state so paste listener attaches reliably)
  const [editorWrapEl, setEditorWrapEl] = useState(null);

  // @mention popup in editor
  const quillRef = useRef(null);
  const outerWrapRef = useRef(null);
  const popoverRef = useRef(null);
  const mentionIndexRef = useRef(null);
  const [mentionPopup, setMentionPopup] = useState(null); // { top, left } | null

  useEffect(() => {
    if (defaultRecipient) {
      setRecipient(defaultRecipient);
    }
  }, [defaultRecipient]);

  useEffect(() => {
    if (isOpen) {
      setIsInternalNote(false);
    } else {
      setMentionPopup(null);
      mentionIndexRef.current = null;
    }
  }, [isOpen]);

  const isEmpty = useMemo(
    () => !stripHtml(newNoteText) && files.length === 0,
    [newNoteText, files.length]
  );

  const closeMentionPopup = useCallback(() => {
    setMentionPopup(null);
    mentionIndexRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    setNewNoteText('');
    setIsInternalNote(false);
    setMentions([]);
    setFiles([]);
    setFileError('');
    closeMentionPopup();
    onClose();
  }, [onClose, closeMentionPopup]);

  /* ---------- FILES ---------- */
  const addFiles = useCallback((incoming) => {
    const list = Array.from(incoming || []).filter(Boolean);
    if (!list.length) return;

    const tooBig = list.filter((f) => f.size > MAX_FILE_SIZE);
    const valid = list.filter((f) => f.size <= MAX_FILE_SIZE);

    setFileError(
      tooBig.length
        ? `${tooBig.length} file(s) skipped: max 25MB each`
        : ''
    );

    if (!valid.length) return;

    setFiles((prev) => {
      const existingKeys = new Set(
        prev.map((f) => `${f.file.name}::${f.file.size}`)
      );
      const next = [...prev];
      valid.forEach((f) => {
        const key = `${f.name}::${f.size}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push({ file: f, id: `${Date.now()}-${Math.random()}` });
        }
      });
      return next;
    });
  }, []);

  const handlePickFiles = () => fileInputRef.current?.click();

  const handleFilesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    addFiles(picked);

    // reset input so same file can be picked again
    e.target.value = '';
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  /* ---------- PASTE FILES (Ctrl+V) INTO EDITOR ---------- */
  useEffect(() => {
    if (!editorWrapEl) return undefined;

    const onPaste = (e) => {
      const cd = e.clipboardData;
      if (!cd) return;

      let pasted = Array.from(cd.files || []);

      if (!pasted.length && cd.items) {
        pasted = Array.from(cd.items)
          .filter((item) => item.kind === 'file')
          .map((item) => item.getAsFile())
          .filter(Boolean);
      }

      if (!pasted.length) return; // normal text paste -> let Quill handle it

      // If clipboard also has real rich text (e.g. copied from Word), let the
      // text paste normally and still attach the files.
      const html = cd.getData('text/html') || '';
      const hasRichText = stripHtml(html).length > 0;

      if (!hasRichText) {
        e.preventDefault();
        e.stopPropagation();
      }

      addFiles(pasted.map(normalizePastedFile));
    };

    // capture phase so we run before Quill's own paste handler
    editorWrapEl.addEventListener('paste', onPaste, true);
    return () => editorWrapEl.removeEventListener('paste', onPaste, true);
  }, [editorWrapEl, addFiles]);

  /* ---------- @ MENTION IN EDITOR ---------- */
  const handleEditorChange = useCallback(
    (value, delta, source) => {
      setNewNoteText(value);

      if (source !== 'user') return;

      const ops = (delta && delta.ops) || [];
      let idx = 0;
      let atIndex = null;

      for (const op of ops) {
        if (op.retain) {
          idx += op.retain;
        } else if (op.insert === '@') {
          atIndex = idx;
          break;
        }
      }

      if (atIndex === null) return;

      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      // only trigger when @ starts a word (not inside an email like a@b.com)
      if (atIndex > 0) {
        const prevChar = quill.getText(atIndex - 1, 1);
        if (prevChar && !/\s/.test(prevChar)) return;
      }

      const bounds = quill.getBounds(atIndex);
      const containerRect = quill.container.getBoundingClientRect();
      const outerRect = outerWrapRef.current?.getBoundingClientRect();
      if (!bounds || !outerRect) return;

      const POPUP_WIDTH = 280;
      let left = containerRect.left - outerRect.left + bounds.left;
      const maxLeft = Math.max(0, outerRect.width - POPUP_WIDTH);
      left = Math.min(Math.max(0, left), maxLeft);

      const top = containerRect.top - outerRect.top + bounds.bottom + 6;

      mentionIndexRef.current = atIndex;
      setMentionPopup({ top, left });
    },
    []
  );

  // focus the picker input as soon as the popup opens
  useEffect(() => {
    if (!mentionPopup) return undefined;

    const t = setTimeout(() => {
      const input = popoverRef.current?.querySelector('input');
      if (input) input.focus();
    }, 50);

    return () => clearTimeout(t);
  }, [mentionPopup]);

  // close popup when clicking outside of it
  useEffect(() => {
    if (!mentionPopup) return undefined;

    const onDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        closeMentionPopup();
      }
    };

    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [mentionPopup, closeMentionPopup]);

  const handlePickMentionFromEditor = (list) => {
    const nextList = Array.isArray(list) ? list : [];
    const prevIds = new Set(mentions.map((m) => m.userId || m.userName));
    const added = nextList.find((m) => !prevIds.has(m.userId || m.userName));

    setMentions(nextList);

    if (!added) return;

    const quill = quillRef.current?.getEditor?.();
    const idx = mentionIndexRef.current;

    if (quill && idx !== null) {
      const label = `@${added.userName}`;

      if (quill.getText(idx, 1) === '@') {
        quill.deleteText(idx, 1, 'user');
      }

      quill.insertText(idx, label, { bold: true, color: '#0056b3' }, 'user');
      quill.insertText(
        idx + label.length,
        ' ',
        { bold: false, color: false },
        'user'
      );
      quill.setSelection(idx + label.length + 1, 0, 'silent');
      quill.focus();
    }

    closeMentionPopup();
  };

  const handlePopoverKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeMentionPopup();
      quillRef.current?.getEditor?.()?.focus();
    }
  };

  const handleSave = async () => {
    const ok = await onSave({
      html: newNoteText,
      subject,
      recipient,
      isInternal: isInternalNote,
      mentions,
      files: files.map((f) => f.file) // raw File objects
    });

    if (ok) {
      setNewNoteText('');
      setIsInternalNote(false);
      setMentions([]);
      setFiles([]);
      setFileError('');
      closeMentionPopup();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        content: styles.modalContent,
        overlay: styles.modalOverlay
      }}
      contentLabel="Add Note Modal"
    >
      <div style={styles.modalHeader}>
        <h3 style={styles.modalTitle}>
          <FiMessageSquare
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
          />
          Add New Note
        </h3>

        <button onClick={handleClose} style={styles.modalCloseBtn}>
          ×
        </button>
      </div>

      <div style={styles.modalBody}>
        <div style={styles.modalFormRow}>
          <div style={styles.modalFormGroup}>
            <label style={styles.modalLabel}>Author</label>
            <input
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              style={styles.modalInput}
              placeholder="Enter your name"
            />
          </div>

          <div style={styles.modalFormGroup}>
            <label style={styles.modalLabel}>Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={styles.modalSelect}
            >
              <option value="case fabrication">Case Fabrication</option>
              <option value="case shipping">Case Shipping</option>
              <option value="case invoice">Case Invoice</option>
              <option value="general">General</option>
            </select>
          </div>
        </div>

        <div style={styles.noteTypeToggle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={styles.noteTypeLabel}>
              <input
                type="checkbox"
                checked={isInternalNote}
                onChange={(e) => setIsInternalNote(e.target.checked)}
                style={{ marginRight: '8px', width: '16px', height: '16px' }}
              />

              {isInternalNote ? (
                <>
                  <FiLock size={14} style={{ marginRight: '4px' }} />
                  Internal Note
                </>
              ) : (
                <>
                  <FiGlobe size={14} style={{ marginRight: '4px' }} />
                  External Note
                </>
              )}
            </label>

            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              {isInternalNote
                ? '(Visible only to team members)'
                : '(Visible to external parties)'}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={styles.modalLabel}>Mention Users (Optional)</label>
          <UserMentionPicker selected={mentions} onChange={setMentions} />
        </div>

        {/* File attachments */}
        <div style={{ marginBottom: '16px' }}>
          <label style={styles.modalLabel}>Attachments (Optional)</label>

          <div style={styles.attachRow}>
            <button
              type="button"
              onClick={handlePickFiles}
              style={styles.attachBtn}
            >
              <FiPaperclip size={14} />
              Attach Files
            </button>

            <span style={styles.attachHint}>
              Images, PDF, Word, Excel, ZIP — max 25MB each. You can also paste
              (Ctrl+V) files/screenshots in the editor.
            </span>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFilesChange}
              style={{ display: 'none' }}
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
            />
          </div>

          {fileError && <div style={styles.fileError}>{fileError}</div>}

          {files.length > 0 && (
            <div style={styles.fileList}>
              {files.map((f) => (
                <div key={f.id} style={styles.fileChip}>
                  <FileThumb file={f.file} />
                  <span style={styles.fileChipName} title={f.file.name}>
                    {f.file.name}
                  </span>
                  <span style={styles.fileChipSize}>
                    {formatFileSize(f.file.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    style={styles.fileChipRemove}
                    title="Remove"
                  >
                    <FiX size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* outer wrapper is position:relative so the @ popup can float over the editor
            without being clipped by editorWrapper's overflow:hidden */}
        <div ref={outerWrapRef} style={styles.editorOuter}>
          <div ref={setEditorWrapEl} style={styles.editorWrapper}>
            <Suspense
              fallback={
                <div
                  style={{
                    ...styles.quillEditor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Spinner animation="border" variant="primary" size="sm" />
                </div>
              }
            >
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={newNoteText}
                onChange={handleEditorChange}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your note here... (type @ to mention someone)"
                style={styles.quillEditor}
              />
            </Suspense>
          </div>

          {mentionPopup && (
            <div
              ref={popoverRef}
              style={{
                ...styles.mentionPopover,
                top: mentionPopup.top,
                left: mentionPopup.left
              }}
              onKeyDown={handlePopoverKeyDown}
            >
              <div style={styles.mentionPopoverTitle}>
                <FiAtSign size={12} /> Mention a user
              </div>
              <UserMentionPicker
                selected={mentions}
                onChange={handlePickMentionFromEditor}
              />
            </div>
          )}
        </div>
      </div>

      <div style={styles.modalFooter}>
        <button onClick={handleClose} style={styles.modalCancelBtn}>
          Cancel
        </button>

        <button
          onClick={handleSave}
          style={{
            ...styles.modalSaveBtn,
            opacity: isEmpty ? 0.5 : 1
          }}
          disabled={isEmpty || isLoading}
        >
          {isLoading ? (
            <Spinner size="sm" animation="border" variant="light" />
          ) : (
            'Save Note'
          )}
        </button>
      </div>
    </Modal>
  );
});

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
function CaseTab({ components, caseId, onProcedureUpdate }) {
  const [key, setKey] = useState('Details');
  const [notes, setNotes] = useState([]);
  const [editorHtml, setEditorHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ NEW: target note id (from ?noteId=) to scroll to & highlight
  const [targetNoteId, setTargetNoteId] = useState(null);
  const highlightedRef = useRef(null);

  const showMessage = useCallback((msg) => {
    setToastMessage(msg);
    setShowToast(true);
  }, []);

  /* CURRENT USER */
  const currentUser = useMemo(() => {
    let userInfo = {};

    try {
      userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch (error) {
      console.error('Error parsing userInfo:', error);
    }

    return {
      userName:
        localStorage.getItem('userName') ||
        userInfo.userName ||
        userInfo.name ||
        'Unknown User',

      userRole:
        localStorage.getItem('userRole') ||
        userInfo.userRole ||
        userInfo.role ||
        'User',

      userId:
        localStorage.getItem('userId') ||
        userInfo.userId ||
        userInfo._id ||
        ''
    };
  }, []);

  const isAdmin = useMemo(
    () => (currentUser.userRole || '').toLowerCase() === 'admin',
    [currentUser.userRole]
  );

  const defaultRecipient = useMemo(
    () => localStorage.getItem('userName') || currentUser.userName || '',
    [currentUser.userName]
  );

  // ✅ NEW: numeric current user id for mention matching
  const myUserIdNum = useMemo(() => {
    const n = Number(currentUser.userId);
    return Number.isNaN(n) ? null : n;
  }, [currentUser.userId]);

  const normalizeNote = useCallback((note) => {
    if (!note) return null;

    return {
      ...note,
      isInternal: toBool(note.isInternal),
      showInternalContent: note.showInternalContent !== false,
      attachments: Array.isArray(note.attachments) ? note.attachments : [],
      // ✅ NEW
      reactions: Array.isArray(note.reactions) ? note.reactions : [],
    };
  }, []);

  const fetchControllerRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!caseId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setIsLoading(true);

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(
        `${API_BASE_URL}/api/cases/${caseId}/notes`,
        {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.status}`);
      }

      const data = await response.json();

      const fetchedNotes = Array.isArray(data.notes) ? data.notes : [];

      const normalizedNotes = sortByCreatedAt(
        fetchedNotes.map(normalizeNote).filter(Boolean)
      );

      setNotes(normalizedNotes);
      setEditorHtml(data.editorHtml || '');
      setIsLoading(false);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error fetching notes:', error);
      showMessage('Failed to fetch notes');
      setIsLoading(false);
    }
  }, [caseId, normalizeNote, showMessage]);

  useEffect(() => {
    fetchNotes();

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
      }
    };
  }, [fetchNotes]);

  // ✅ NEW: read ?noteId= on mount, switch to Notes tab immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const noteId = params.get('noteId');
    if (noteId) {
      setTargetNoteId(noteId);
      setKey('Notes');
    }
  }, []);

  // ✅ NEW: once Notes tab active AND notes loaded → scroll & highlight target note
  useEffect(() => {
    if (key !== 'Notes' || !targetNoteId || notes.length === 0) return;

    const timer = setTimeout(() => {
      const el = document.getElementById(targetNoteId);
      if (!el) return;

      // clear previous outline
      if (highlightedRef.current && highlightedRef.current !== el) {
        highlightedRef.current.style.outline = '';
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.outline = '3px solid #ffc107';
      highlightedRef.current = el;

      setTimeout(() => {
        if (highlightedRef.current === el) {
          el.style.outline = '';
          highlightedRef.current = null;
        }
      }, 5000);
    }, 400);

    return () => clearTimeout(timer);
  }, [key, targetNoteId, notes]);

  const toggleNoteVisibility = useCallback(
    async (noteId) => {
      if (!isAdmin) {
        showMessage('Only administrators can hide/show notes');
        return;
      }

      if (!noteId || !caseId) {
        showMessage('Invalid note');
        return;
      }

      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}/api/cases/${caseId}/notes/${noteId}/toggle-visibility`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              userId: currentUser.userId,
              userName: currentUser.userName
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to toggle visibility: ${response.status}`);
        }

        const data = await response.json();

        if (data.note) {
          const updatedNote = normalizeNote(data.note);

          setNotes((prevNotes) =>
            prevNotes.map((note) =>
              note._id === noteId ? { ...note, ...updatedNote } : note
            )
          );
        } else {
          await fetchNotes();
        }

        showMessage(data.message || 'Note visibility updated');
      } catch (error) {
        console.error('Error toggling note visibility:', error);
        showMessage('Failed to update note visibility');
      }
    },
    [
      isAdmin,
      caseId,
      currentUser.userId,
      currentUser.userName,
      normalizeNote,
      fetchNotes,
      showMessage
    ]
  );

  const toggleInternalNoteVisibility = useCallback((noteId, e) => {
    if (e) {
      e.stopPropagation();
    }

    setNotes((prevNotes) =>
      prevNotes.map((note) =>
        note._id === noteId
          ? { ...note, showInternalContent: note.showInternalContent === false }
          : note
      )
    );
  }, []);

  // ✅ NEW: update a single note's reactions after the API call succeeds
  const handleReactionsUpdated = useCallback((noteId, reactions) => {
    setNotes((prevNotes) =>
      prevNotes.map((n) => (n._id === noteId ? { ...n, reactions } : n))
    );
  }, []);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const addNote = useCallback(
    async ({ html, subject, recipient, isInternal, mentions, files }) => {
      const filesArr = Array.isArray(files) ? files : [];
      const hasText = !!stripHtml(html);

      if (!hasText && filesArr.length === 0) {
        showMessage('Note cannot be empty');
        return false;
      }

      if (!caseId) {
        showMessage('Case ID is missing');
        return false;
      }

      setIsLoading(true);

      try {
        const formattedNote = `
        <div style="
          font-weight: bold;
          margin-bottom: 15px;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        ">
          From: ${currentUser.userName || recipient || 'Unknown User'}
          ${currentUser.userRole ? `(${currentUser.userRole})` : ''}
          <br/>

          Created:
          ${new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}

          ${subject ? ` for ${subject}` : ''}
        </div>

        ${html || ''}
      `;

        const token = localStorage.getItem('accessToken');
        const mentionedUserIds = (mentions || []).map((m) => m.userId);

        let response;

        if (filesArr.length > 0) {
          // === multipart/form-data path ===
          const formData = new FormData();
          formData.append('text', formattedNote);
          formData.append('createdBy', recipient || currentUser.userName || 'Unknown User');
          formData.append('isInternal', String(!!isInternal));
          formData.append('createdByUserId', currentUser.userId || '');
          formData.append('subject', subject || 'case fabrication');
          formData.append('mentionedUserIds', JSON.stringify(mentionedUserIds));
          formData.append('userRole', currentUser.userRole || 'User');

          filesArr.forEach((f) => {
            formData.append('attachments', f);
          });

          response = await fetch(
            `${API_BASE_URL}/api/cases/${caseId}/notes`,
            {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              // NOTE: do NOT set Content-Type; browser sets boundary
              body: formData
            }
          );
        } else {
          // === original JSON path (unchanged) ===
          response = await fetch(
            `${API_BASE_URL}/api/cases/${caseId}/notes`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              },
              body: JSON.stringify({
                text: formattedNote,
                createdBy: recipient || currentUser.userName || 'Unknown User',
                isInternal,
                createdByUserId: currentUser.userId || '',
                subject: subject || 'case fabrication',
                mentionedUserIds
              })
            }
          );
        }

        if (!response.ok) {
          let backendMessage = `Failed to add note: ${response.status}`;

          try {
            const errorData = await response.json();

            if (errorData?.message) {
              backendMessage = errorData.message;
            }

            if (errorData?.error) {
              backendMessage = errorData.error;
            }
          } catch (jsonError) {
            console.error('Could not parse backend error:', jsonError);
          }

          throw new Error(backendMessage);
        }

        const data = await response.json();

        if (data.newNote) {
          const savedNote = normalizeNote(data.newNote);

          setNotes((prevNotes) => sortByCreatedAt([...prevNotes, savedNote]));
        } else {
          await fetchNotes();
        }

        showMessage('Note added successfully');
        setIsModalOpen(false);

        return true;
      } catch (error) {
        console.error('Error adding note:', error);

        showMessage(error.message || 'Failed to add note');

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      caseId,
      currentUser.userName,
      currentUser.userRole,
      currentUser.userId,
      normalizeNote,
      fetchNotes,
      showMessage
    ]
  );

  const safeEditorHtml = useMemo(
    () => (editorHtml ? DOMPurify.sanitize(editorHtml) : ''),
    [editorHtml]
  );

  const totalNotesCount = notes.length + (editorHtml ? 1 : 0);

  return (
    <div style={styles.container}>
      <style>
        {`
          @media (max-width: 767px) {

            .case-tabs-wrapper {
              width: 100%;
              overflow-x: auto;
              overflow-y: hidden;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
            }

            .case-tabs-wrapper::-webkit-scrollbar {
              display: none;
            }

            .case-tabs-wrapper .custom-tabs {
              display: flex !important;
              flex-wrap: nowrap !important;
              width: max-content !important;
              min-width: 100% !important;
              margin-bottom: 8px !important;
            }

            .case-tabs-wrapper .custom-tabs .nav-item {
              flex: 0 0 auto !important;
              white-space: nowrap !important;
            }

            .case-tabs-wrapper .custom-tabs .nav-link {
              white-space: nowrap !important;
              font-size: 10px !important;
              padding: 6px 9px !important;
              margin: 0 !important;
              line-height: 1.2 !important;
            }

            .case-tabs-wrapper .custom-tabs .nav-link svg {
              width: 11px;
              height: 11px;
            }

            .case-tabs-wrapper .notes-mobile {
              padding: 10px !important;
            }

            .case-modal-mobile {
              width: 94% !important;
              max-width: 94% !important;
            }

            .case-modal-mobile .ql-editor {
              min-height: 180px !important;
            }

          }

          @media (max-width: 400px) {

            .case-tabs-wrapper .custom-tabs .nav-link {
              font-size: 9px !important;
              padding: 5px 7px !important;
            }

          }
        `}
      </style>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={3000}
        autohide
        style={styles.toast}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>

      <div className="case-tabs-wrapper">
        <Tabs
          id="controlled-tab"
          activeKey={key}
          onSelect={(k) => {
            if (k) {
              setKey(k);
            }
          }}
          className="mb-3 custom-tabs"
          style={styles.tabs}
          mountOnEnter
          unmountOnExit={false}
        >
          <Tab
            eventKey="Details"
            title="Details"
            style={styles.tabPane}
          >
            <div style={styles.tabContent}>
              <CaseTable onProcedureUpdate={onProcedureUpdate} />
            </div>
          </Tab>

          <Tab
            eventKey="Notes"
            title={`Notes (${totalNotesCount})`}
            style={styles.tabPane}
          >
            <div
              className="notes-mobile"
              style={styles.notesContainer}
            >
              {isAdmin ? (
                <div style={styles.adminInfoBar}>
                  <FiShield size={14} />
                  <span>
                    Admin Mode: You can hide notes from all users.
                    Hidden notes are marked with red border.
                  </span>
                </div>
              ) : (
                <div style={styles.userInfoBar}>
                  <FiUser size={14} />
                  <span>
                    User Mode: You can only view notes visible to you.
                  </span>
                </div>
              )}

              <div style={styles.notesHeader}>
                <div style={styles.title}>
                  <SlNote size={18} color="#0066cc" />
                  Case Notes
                  <span style={styles.count}>
                    {totalNotesCount}
                  </span>
                </div>

                <button
                  style={styles.btnAddNote}
                  onClick={openModal}
                >
                  <FiPlus size={16} />
                  Add Note
                </button>
              </div>

              {isLoading && notes.length === 0 && !editorHtml ? (
                <div style={styles.loadingContainer}>
                  <Spinner animation="border" variant="primary" />
                  <p
                    style={{
                      marginTop: '12px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}
                  >
                    Loading notes...
                  </p>
                </div>
              ) : (
                <>
                  {editorHtml && (
                    <div style={styles.editorHtmlNote}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: safeEditorHtml
                        }}
                      />
                    </div>
                  )}

                  {notes.length === 0
                    ? !editorHtml && (
                        <div style={styles.emptyState}>
                          <FiFileText
                            size={24}
                            style={{
                              display: 'block',
                              margin: '0 auto 8px',
                              color: '#adb5bd'
                            }}
                          />
                          No notes yet
                        </div>
                      )
                    : notes.map((note) => {
                        // ✅ NEW: only mark as "mentioned you" if the current user
                        //     appears in the note's mentions
                        const isMentionedForMe =
                          myUserIdNum !== null &&
                          Array.isArray(note.mentions) &&
                          note.mentions.some(
                            (m) => m && Number(m.userId) === myUserIdNum
                          );

                        return (
                          <NoteItem
                            key={note._id}
                            note={note}
                            isAdmin={isAdmin}
                            isMentionedForMe={isMentionedForMe}
                            onToggleVisibility={toggleNoteVisibility}
                            onToggleInternal={toggleInternalNoteVisibility}
                            caseId={caseId}
                            myUserIdNum={myUserIdNum}
                            onReactionsUpdated={handleReactionsUpdated}
                          />
                        );
                      })}
                </>
              )}
            </div>
          </Tab>

          <Tab
            eventKey="Tasks"
            title="Tasks"
            disabled
            style={styles.tabPane}
          >
            <div style={styles.tabContent}>
              <Task />
            </div>
          </Tab>

          <Tab
            eventKey="Alert"
            title="Alerts"
            disabled
            style={styles.tabPane}
          >
            <div style={styles.tabContent}>
              <Alert setActiveTab={setKey} />
            </div>
          </Tab>
        </Tabs>
      </div>

      <AddNoteModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={addNote}
        isLoading={isLoading}
        defaultRecipient={defaultRecipient}
      />
    </div>
  );
}

const styles = {
  container: {
    margin: 0,
    padding: 0,
    width: '100%',
    maxWidth: '100%'
  },

  toast: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999
  },

  tabs: {
    backgroundColor: '#f8f9fa',
    borderTop: '3px solid #0066cc',
    padding: 0,
    margin: 0,
    borderRadius: 0,
    width: '100%',
    maxWidth: '100%'
  },

  tabPane: {
    padding: 0,
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },

  tabContent: {
    padding: 0,
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },

  notesContainer: {
    backgroundColor: '#f8f9fa',
    padding: '24px',
    borderTop: '1px solid #e9ecef',
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },

  adminInfoBar: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  userInfoBar: {
    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    color: '#1a237e',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  notesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e9ecef'
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#212529'
  },

  count: {
    background: '#e9ecef',
    color: '#495057',
    padding: '0 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600
  },

  btnAddNote: {
    background: '#0066cc',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease'
  },

  loadingContainer: {
    textAlign: 'center',
    padding: '40px',
    background: 'white',
    borderRadius: '4px',
    border: '1px solid #e9ecef'
  },

  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#868e96',
    fontSize: '14px',
    border: '1px dashed #dee2e6',
    borderRadius: '4px'
  },

  addNoteFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '2px',
    paddingRight: '10px'
  },

  noteItem: {
    background: 'white',
    borderRadius: '4px',
    padding: '16px 20px',
    marginBottom: '8px',
    borderLeft: '3px solid #e9ecef',
    transition: 'all 0.2s ease',
    position: 'relative'
  },

  // ✅ NEW: subtle persistent ring for mentioned-you notes
  noteItemMentioned: {
    boxShadow: '0 0 0 2px #ffe08a, 0 4px 14px rgba(255, 193, 7, 0.25)'
  },

  // ✅ NEW: "Mentioned you" chip
  mentionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: '#ffc107',
    color: '#212529',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: '8px'
  },

  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#6c757d',
    flexWrap: 'wrap'
  },

  separator: {
    color: '#dee2e6'
  },

  noteContent: {
    fontSize: '14px',
    color: '#212529',
    lineHeight: '1.6',
    position: 'relative'
  },

  noteActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #f1f3f5'
  },

  noteBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },

  internalBadge: {
    background: '#fff3cd',
    color: '#856404'
  },

  externalBadge: {
    background: '#d1ecf1',
    color: '#0c5460'
  },

  hiddenBadge: {
    background: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb'
  },

  adminBadge: {
    background: '#e8eaf6',
    color: '#283593'
  },

  adminOverlay: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: '#dc3545',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    zIndex: 10
  },

  actionBtnAdmin: {
    background: 'none',
    border: 'none',
    color: '#dc3545',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '2px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  hiddenStatus: {
    fontSize: '11px',
    color: '#721c24',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  visibleStatus: {
    fontSize: '11px',
    color: '#283593',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  internalToggleCheckbox: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: '10px',
    userSelect: 'none'
  },

  hiddenInternalNote: {
    padding: '12px',
    background: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '6px',
    color: '#856404',
    textAlign: 'center',
    fontSize: '13px'
  },

  blurContent: {
    filter: 'blur(5px)',
    opacity: 0.3,
    transition: 'all 0.3s ease',
    pointerEvents: 'none',
    userSelect: 'none'
  },

  hiddenOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    zIndex: 5,
    padding: '20px'
  },

  editorHtmlNote: {
    background: 'white',
    borderRadius: '4px',
    padding: '16px 20px',
    marginBottom: '8px',
    borderLeft: '3px solid #17a2b8',
    position: 'relative'
  },

  modalContent: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    maxWidth: '95%',
    padding: 0,
    borderRadius: '8px',
    border: 'none',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
  },

  modalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },

  modalHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef',
    background: '#f8f9fa',
    borderRadius: '8px 8px 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },

  modalTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#212529'
  },

  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#868e96',
    padding: '0 4px'
  },

  modalBody: {
    padding: '24px',
    maxHeight: '70vh',
    overflowY: 'auto'
  },

  modalFormRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px'
  },

  modalFormGroup: {
    flex: 1
  },

  modalLabel: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 500,
    color: '#495057',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },

  modalInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s'
  },

  modalSelect: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none'
  },

  noteTypeToggle: {
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e9ecef'
  },

  noteTypeLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#495057',
    fontSize: '13px'
  },

  /* outer wrapper: positioning context for the @ mention popup */
  editorOuter: {
    position: 'relative'
  },

  editorWrapper: {
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    overflow: 'hidden'
  },

  quillEditor: {
    height: '300px'
  },

  /* @ mention popup */
  mentionPopover: {
    position: 'absolute',
    zIndex: 30,
    width: '280px',
    background: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    padding: '8px'
  },

  mentionPopoverTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: '6px'
  },

  modalFooter: {
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    borderTop: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa',
    borderRadius: '0 0 8px 8px'
  },

  modalCancelBtn: {
    backgroundColor: 'white',
    color: '#495057',
    border: '1px solid #dee2e6',
    padding: '8px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.2s ease'
  },

  modalSaveBtn: {
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    padding: '8px 24px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.2s ease'
  },

  /* ===== Attachment UI styles ===== */
  attachRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },

  attachBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#eef4fb',
    color: '#0066cc',
    border: '1px dashed #8fc1f0',
    padding: '8px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s ease'
  },

  attachHint: {
    fontSize: '11px',
    color: '#868e96'
  },

  fileError: {
    marginTop: '6px',
    fontSize: '11px',
    color: '#dc3545'
  },

  fileList: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },

  fileChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12px'
  },

  fileChipThumb: {
    width: '28px',
    height: '28px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    background: '#fff',
    flex: '0 0 auto'
  },

  fileChipName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#212529'
  },

  fileChipSize: {
    color: '#868e96',
    fontSize: '11px'
  },

  fileChipRemove: {
    background: 'none',
    border: 'none',
    color: '#dc3545',
    cursor: 'pointer',
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px'
  },

  attachmentsWrap: {
    marginTop: '12px',
    borderTop: '1px dashed #e9ecef',
    paddingTop: '10px'
  },

  attachmentsLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#495057',
    marginBottom: '8px'
  },

  attachmentsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '10px'
  },

  attachmentCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '6px 8px',
    textDecoration: 'none'
  },

  /* large image preview card */
  attachmentCardImage: {
    display: 'flex',
    flexDirection: 'column',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    overflow: 'hidden'
  },

  attachmentThumbLinkLarge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    background: '#f1f3f5',
    cursor: 'zoom-in'
  },

  attachmentThumbLarge: {
    width: '100%',
    height: '220px',
    objectFit: 'contain',
    display: 'block'
  },

  attachmentImageFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderTop: '1px solid #e9ecef',
    background: '#fff'
  },

  attachmentThumbLink: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  attachmentThumb: {
    width: '38px',
    height: '38px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    background: '#fff'
  },

  attachmentIconBox: {
    width: '38px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#eef4fb',
    borderRadius: '4px',
    flex: '0 0 auto'
  },

  attachmentInfo: {
    flex: 1,
    minWidth: 0
  },

  attachmentName: {
    fontSize: '12px',
    color: '#212529',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  attachmentMeta: {
    fontSize: '11px',
    color: '#868e96'
  },

  attachmentDownload: {
    color: '#0066cc',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '4px',
    textDecoration: 'none'
  }
};

export default React.memo(CaseTab);